import * as R from "ramda";
import { ACTION_TYPE } from "../../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../../core/redux/api/circuitBreaker/module";
import { apiGetWeeklyVisitorReports } from "../../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../../core/redux/IAppState";
import { assoc, isNilOrEmpty } from "../../language/module";
import { getResource } from "../../language/resources/getResource";
import { WeeklyVisitorDataReport } from "../models";


export const KEY_ACTION = ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_REQUEST,
  ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_RESPONSE,
  ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_ERROR,
];

const DEFAULT_NUMBER_OF_FILES = 4;

/**
 * An action to dispatch to get a set of URLS for weekly visitor reports for the specified resort
 */
export const getWeeklyVisitorDataReportsAction = (
  resortAbbrev: string,
  numberOfFiles: number
) =>
  makeApiAction({
    endpoint: apiGetWeeklyVisitorReports(
      resortAbbrev,
      numberOfFiles ?? DEFAULT_NUMBER_OF_FILES,
    ),
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { resortAbbrev },
    })) as RsaaTypes,
    // Bail if already loading results for that query
    bailout: (state: IAppState) =>
      hasTrippedBreaker(state, KEY_ACTION) ||
      (R.path(["selectedResort", "resortAbbreviation"], state) ===
        resortAbbrev &&
        !!getLoading(state, KEY_ACTION, KEY_ACTION)),
  });

export const reducer: ApiReducer<WeeklyVisitorDataReport[]> = (
  state,
  action,
  error,
  isLoading,
  payload
) => {
  const resortAbbrev = action.meta.resortAbbrev;
  const appStateResort = R.path<string>(
    ["selectedResort", "resortAbbreviation"],
    state
  );

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts
  if (
    action.type === ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_RESPONSE &&
    !isNilOrEmpty(appStateResort) &&
    appStateResort !== resortAbbrev
  ) {
    return state;
  }

  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      KEY_ACTION,
      KEY_ACTION,
      error ??
        getResource(state.locale, "GET_WEEKLY_VISITOR_DATA_REPORTS_ERROR")
    );
  }

  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    KEY_ACTION,
    KEY_ACTION
  );

  return assoc("weeklyVisitorDataReports", payload, noLoadingOrError);
};

export const getWeeklyVisitorDataReportsReducer =
  makeApiReducer<WeeklyVisitorDataReport[]>(ACTIONS, KEY_ACTION, reducer);
